import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { getLevelInfo } from "@/lib/levelUtils";

interface CustomerEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: {
    id: string;
    full_name: string | null;
    email: string;
    phone: string | null;
    level: number | null;
    user_id: string | null;
    [key: string]: unknown;
  };
}

type FormState = Record<string, string | boolean | null>;

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function boolVal(v: unknown): boolean {
  return v === true;
}

function CollapsibleSection({ title, children, defaultOpen }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          {title}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pb-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function FormField({ label, value, onChange, type = "text", placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-8 text-sm" />
    </div>
  );
}

export default function CustomerEditDialog({ open, onOpenChange, customer }: CustomerEditDialogProps) {
  const { t } = useTranslation("admin");
  const { user: adminUser } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({});

  useEffect(() => {
    if (open) {
      setForm({
        full_name: str(customer.full_name),
        email: str(customer.email),
        phone: str(customer.phone),
        level: customer.level != null ? String(customer.level) : "",
        // Personal
        gender: str(customer.gender),
        personal_number: str(customer.personal_number),
        date_of_birth: str(customer.date_of_birth),
        customer_type: str(customer.customer_type) || "individual",
        // Address
        address: str(customer.address),
        address2: str(customer.address2),
        postal_code: str(customer.postal_code),
        city: str(customer.city),
        country: str(customer.country) || "SE",
        // Company
        company_name: str(customer.company_name),
        company_contact: str(customer.company_contact),
        // Invoice
        invoice_address: str(customer.invoice_address),
        invoice_address2: str(customer.invoice_address2),
        invoice_postal_code: str(customer.invoice_postal_code),
        invoice_city: str(customer.invoice_city),
        invoice_contact: str(customer.invoice_contact),
        invoice_phone: str(customer.invoice_phone),
        invoice_email: str(customer.invoice_email),
        // Guardian
        guardian_name: str(customer.guardian_name),
        guardian_email: str(customer.guardian_email),
        guardian_phone: str(customer.guardian_phone),
        guardian2_name: str(customer.guardian2_name),
        guardian2_email: str(customer.guardian2_email),
        guardian2_phone: str(customer.guardian2_phone),
        // MATCHi
        matchi_customer_nr: str(customer.matchi_customer_nr),
        matchi_id: str(customer.matchi_id),
        // Other
        groups: str(customer.groups),
        wants_marketing: boolVal(customer.wants_marketing),
      });
    }
  }, [open, customer]);

  const set = (key: string) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  const setBool = (key: string) => (value: boolean) => setForm((f) => ({ ...f, [key]: value }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const toNull = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);
      const payload: Record<string, unknown> = {
        full_name: toNull(form.full_name),
        email: (form.email as string).trim(),
        phone: toNull(form.phone),
        level: form.level ? parseFloat(form.level as string) : null,
        gender: toNull(form.gender),
        personal_number: toNull(form.personal_number),
        date_of_birth: toNull(form.date_of_birth),
        customer_type: toNull(form.customer_type),
        address: toNull(form.address),
        address2: toNull(form.address2),
        postal_code: toNull(form.postal_code),
        city: toNull(form.city),
        country: toNull(form.country),
        company_name: toNull(form.company_name),
        company_contact: toNull(form.company_contact),
        invoice_address: toNull(form.invoice_address),
        invoice_address2: toNull(form.invoice_address2),
        invoice_postal_code: toNull(form.invoice_postal_code),
        invoice_city: toNull(form.invoice_city),
        invoice_contact: toNull(form.invoice_contact),
        invoice_phone: toNull(form.invoice_phone),
        invoice_email: toNull(form.invoice_email),
        guardian_name: toNull(form.guardian_name),
        guardian_email: toNull(form.guardian_email),
        guardian_phone: toNull(form.guardian_phone),
        guardian2_name: toNull(form.guardian2_name),
        guardian2_email: toNull(form.guardian2_email),
        guardian2_phone: toNull(form.guardian2_phone),
        matchi_customer_nr: toNull(form.matchi_customer_nr),
        matchi_id: toNull(form.matchi_id),
        groups: toNull(form.groups),
        wants_marketing: form.wants_marketing,
      };

      const { error: custErr } = await supabase
        .from("customers")
        .update(payload as Record<string, unknown>)
        .eq("id", customer.id);
      if (custErr) throw custErr;

      if (customer.user_id) {
        const newLevel = form.level ? parseInt(form.level as string) : null;
        const oldLevel = customer.level;
        const levelChanged = newLevel !== oldLevel && newLevel != null;

        // INTENTIONAL: bucket-C admin-PII write via "Admin all profiles" RLS policy.
        // Admin edits the linked profile of a customer record (full_name, phone, level).
        const { error: profErr } = await supabase
          .from("profiles")
          .update({
            full_name: toNull(form.full_name),
            phone: toNull(form.phone),
            level: newLevel,
            ...(levelChanged ? { level_source: "admin_override", level_assessed_at: new Date().toISOString() } : {}),
          } as Record<string, unknown>)
          .eq("id", customer.user_id);
        if (profErr) throw profErr;

        // Audit log for level change
        if (levelChanged && adminUser) {
          await supabase.from("level_change_log" as any).insert({
            user_id: customer.user_id,
            organization_id: (customer as any).organization_id,
            old_level: oldLevel,
            new_level: newLevel,
            changed_by: adminUser.id,
            change_source: "admin_override",
            reason: "Ändrad via kunddetalj",
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer", customer.id] });
      queryClient.invalidateQueries({ queryKey: ["admin-customers"] });
      toast.success(t("customer.editDialog.customerUpdated"));
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err.message || t("customer.editDialog.saveError"));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("customersPage.editCustomer")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Basic info */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground">{t("customerFields.basicInfo")}</p>
            <FormField label={t("customerForm.nameLabel")} value={str(form.full_name)} onChange={set("full_name")} />
            <FormField label={t("customerForm.emailLabel")} value={str(form.email)} onChange={set("email")} type="email" />
            <FormField label={t("customerForm.phoneLabel")} value={str(form.phone)} onChange={set("phone")} placeholder="07X XXX XX XX" />
            <div className="space-y-1">
              <Label className="text-xs">{t("customerForm.levelLabel")}</Label>
              <Select value={str(form.level) || ""} onValueChange={set("level")}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => {
                    const lvl = i + 1;
                    const info = getLevelInfo(lvl);
                    return <SelectItem key={lvl} value={String(lvl)}>{lvl} — {t(`common:levelInfo.${info.labelKey}`, info.label)}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Personal details */}
          <CollapsibleSection title={t("customerFields.personalDetails")} defaultOpen={!!(form.gender || form.personal_number || form.date_of_birth)}>
            <div className="space-y-1">
              <Label className="text-xs">{t("customerFields.gender")}</Label>
              <Select value={str(form.gender)} onValueChange={set("gender")}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t("customerFields.male")}</SelectItem>
                  <SelectItem value="female">{t("customerFields.female")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FormField label={t("customerFields.personalNumber")} value={str(form.personal_number)} onChange={set("personal_number")} placeholder="YYYYMMDD-XXXX" />
            <FormField label={t("customerFields.dateOfBirth")} value={str(form.date_of_birth)} onChange={set("date_of_birth")} type="date" />
            <div className="space-y-1">
              <Label className="text-xs">{t("customerFields.customerType")}</Label>
              <Select value={str(form.customer_type) || "individual"} onValueChange={set("customer_type")}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">{t("customerFields.individual")}</SelectItem>
                  <SelectItem value="organization">{t("customerFields.organization")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CollapsibleSection>

          {/* Address */}
          <CollapsibleSection title={t("customerFields.address")} defaultOpen={!!(form.address || form.city)}>
            <FormField label={t("customerFields.addressLine1")} value={str(form.address)} onChange={set("address")} />
            <FormField label={t("customerFields.addressLine2")} value={str(form.address2)} onChange={set("address2")} />
            <div className="grid grid-cols-2 gap-2">
              <FormField label={t("customerFields.postalCode")} value={str(form.postal_code)} onChange={set("postal_code")} />
              <FormField label={t("customerFields.city")} value={str(form.city)} onChange={set("city")} />
            </div>
            <FormField label={t("customerFields.country")} value={str(form.country)} onChange={set("country")} />
          </CollapsibleSection>

          {/* Company (only when organization) */}
          {form.customer_type === "organization" && (
            <CollapsibleSection title={t("customerFields.company")} defaultOpen>
              <FormField label={t("customerFields.companyName")} value={str(form.company_name)} onChange={set("company_name")} />
              <FormField label={t("customerFields.companyContact")} value={str(form.company_contact)} onChange={set("company_contact")} />
            </CollapsibleSection>
          )}

          {/* Invoice */}
          <CollapsibleSection title={t("customerFields.invoicing")} defaultOpen={!!(form.invoice_address || form.invoice_email)}>
            <FormField label={t("customerFields.invoiceAddress")} value={str(form.invoice_address)} onChange={set("invoice_address")} />
            <FormField label={t("customerFields.addressLine2")} value={str(form.invoice_address2)} onChange={set("invoice_address2")} />
            <div className="grid grid-cols-2 gap-2">
              <FormField label={t("customerFields.invoicePostalCode")} value={str(form.invoice_postal_code)} onChange={set("invoice_postal_code")} />
              <FormField label={t("customerFields.invoiceCity")} value={str(form.invoice_city)} onChange={set("invoice_city")} />
            </div>
            <FormField label={t("customerFields.invoiceContact")} value={str(form.invoice_contact)} onChange={set("invoice_contact")} />
            <FormField label={t("customerFields.invoicePhone")} value={str(form.invoice_phone)} onChange={set("invoice_phone")} />
            <FormField label={t("customerFields.invoiceEmail")} value={str(form.invoice_email)} onChange={set("invoice_email")} type="email" />
          </CollapsibleSection>

          {/* Guardian */}
          <CollapsibleSection title={t("customerFields.guardian")} defaultOpen={!!form.guardian_name}>
            <p className="text-xs font-medium text-muted-foreground">{t("customerFields.guardian1")}</p>
            <FormField label={t("customerFields.guardianName")} value={str(form.guardian_name)} onChange={set("guardian_name")} />
            <FormField label={t("customerFields.guardianEmail")} value={str(form.guardian_email)} onChange={set("guardian_email")} type="email" />
            <FormField label={t("customerFields.guardianPhone")} value={str(form.guardian_phone)} onChange={set("guardian_phone")} />
            <p className="text-xs font-medium text-muted-foreground pt-2">{t("customerFields.guardian2")}</p>
            <FormField label={t("customerFields.guardianName")} value={str(form.guardian2_name)} onChange={set("guardian2_name")} />
            <FormField label={t("customerFields.guardianEmail")} value={str(form.guardian2_email)} onChange={set("guardian2_email")} type="email" />
            <FormField label={t("customerFields.guardianPhone")} value={str(form.guardian2_phone)} onChange={set("guardian2_phone")} />
          </CollapsibleSection>

          {/* Other */}
          <CollapsibleSection title={t("customerFields.other")} defaultOpen={!!(form.groups || form.wants_marketing)}>
            <FormField label={t("customerFields.groups")} value={str(form.groups)} onChange={set("groups")} placeholder="Seriespel VT26, Nybörjare" />
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t("customerFields.wantsMarketing")}</Label>
              <Switch checked={boolVal(form.wants_marketing)} onCheckedChange={setBool("wants_marketing")} />
            </div>
          </CollapsibleSection>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("customer.editDialog.cancel")}</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={!(form.email as string)?.trim() || saveMutation.isPending}>
            {saveMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("customer.editDialog.saving")}...</> : t("customer.editDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
